import { Slot, Slottable } from '@radix-ui/react-slot'

import { Icon } from '../icon'
import { Tag } from '../tag'

import { cn } from '~/utils/cn'

import { listStyles } from './list-item.styles'
import type { ListItemProps } from './list-item.types'

export function ListItem({
  children,
  title,
  description,
  disabled = false,
  tag,
  leftIcon,
  showSeparator = false,
  asChild = false,
  ...props
}: ListItemProps) {
  const Element = asChild ? Slot : 'a'

  const {
    root: rootClass,
    header: headerClass,
    title: titleClass,
    description: descriptionClass,
    separator: separatorClass,
  } = listStyles({
    isDisabled: disabled,
  })

  const iconColor = disabled
    ? 'colorTextNeutralDisabled'
    : 'colorTextNeutralDefault'

  const shouldRenderTag = !disabled && tag.enabled

  return (
    <li className={cn('inline-flex w-full list-none')}>
      <div
        role="button"
        aria-disabled={disabled}
        className={cn(
          rootClass({
            className: showSeparator && separatorClass(),
          })
        )}
      >
        <header className={cn(headerClass())}>
          {leftIcon && (
            <Icon
              size="small"
              color={iconColor}
              symbol={leftIcon}
              aria-hidden
            />
          )}

          <div
            className={cn(
              'grid flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2'
            )}
          >
            <h3 className={cn(titleClass())}>
              {title}

              <Element
                {...props}
                className={cn(
                  disabled
                    ? 'hidden'
                    : 'absolute inset-0 outline-0 focus-visible:ring-[length:var(--border-width-halfpulse)] focus-visible:ring-[var(--color-container-border-focused)]'
                )}
                tabIndex={0}
              >
                <Slottable>{children}</Slottable>
              </Element>
            </h3>

            {shouldRenderTag && (
              <Tag.Root variants="principal">
                <Tag.Label>{tag.label}</Tag.Label>
              </Tag.Root>
            )}
          </div>

          <Icon
            size="extra-small"
            symbol="rdicon-chevron-right"
            color={iconColor}
            aria-hidden
          />
        </header>

        {description && <p className={cn(descriptionClass())}>{description}</p>}
      </div>
    </li>
  )
}

export type { ListItemProps }
